import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {forkJoin, from, iif, Observable, of} from 'rxjs';
import {filter, map, switchMap, tap} from 'rxjs/operators';
import {BaseService} from '@shared/services/base.service';
import {MapService} from '@shared/services/map.service';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import LayerView from '@arcgis/core/views/layers/LayerView';
import MapView from '@arcgis/core/views/MapView';

@Injectable({
  providedIn: 'root'
})
export class WetlandsService extends BaseService {
  public item: any = {};
  public meta: any = {};
  layers: { [key: string]: FeatureLayer } = {}
  mapService = inject(MapService);
  highlightHandle: { [key: string]: __esri.Handle } = {};

  constructor(public http: HttpClient) {
    super(`${environment.baseUrl}/wetlands/attributes`, http);
  }

  loadLayers(view: MapView) {
    this.layers.points = 'points' in this.layers ? this.layers.points : this.mapService.getLayerFromMap(view.map, `${environment.wetlands_service}/0`) as FeatureLayer;
    this.layers.lines = 'lines' in this.layers ? this.layers.lines : this.mapService.getLayerFromMap(view.map, `${environment.wetlands_service}/1`) as FeatureLayer;
    this.layers.polygons = 'polygons' in this.layers ? this.layers.polygons : this.mapService.getLayerFromMap(view.map, `${environment.wetlands_service}/2`) as FeatureLayer;
  }

  loadFeatures(view: MapView, layerKey: string, item) {
    return forkJoin({
      layerView: from(view.whenLayerView(this.layers[layerKey])),
      featureQuery: from(this.layers[layerKey].queryFeatures({
        where: `attributes_fk = '${item.globalid}'`,
        outFields: ['*']
      }))
    }).pipe(
      tap(({layerView, featureQuery}) => {
        if (this.highlightHandle[layerKey]) {
          this.highlightHandle[layerKey].remove();
        }
        this.highlightHandle[layerKey] = layerView.highlight(featureQuery.features);
      })
    )
  }

  highlightFeatures(item) {
    return this.mapService.getView().pipe(
      switchMap(view => {
        this.loadLayers(view);
        return forkJoin([
          this.loadFeatures(view, 'points', item),
          this.loadFeatures(view, 'lines', item),
          this.loadFeatures(view, 'polygons', item)
        ]);
      }),
    );
  }

  zoomToFeature(item, layer: FeatureLayer) {
    this.mapService.getView().pipe(
      map(view => {
        layer.queryFeatures({
          where: `globalid = '${item.globalid}'`,
          returnGeometry: true
        }).then(r => {
          view.goTo(r.features);
        })
      })
    ).subscribe();
  }

  public loadData(search: any): Observable<any> {
    return this.getList<any>(search).pipe(
      tap(response => {
        if (response.results[0]) {
          this.item = response.results[0];
        } else {
          this.item = {};
        }
        this.meta = response.meta;
        this.meta.display_fields = ['surveyor', 'survey_date'];
        this.meta.type = 'wetlands/attributes';
        this.meta.urlPath = 'wetlands';

        this.meta.subItems = ['attachments', 'points', 'lines', 'polygons'];
        this.meta.fields.attachments = {
          type: 'wetlands/attachments',
          verbose_name_plural: 'Attachments',
          post_upload: (attachments) => {
            this.meta.subItems = [];
            this.loadData({search: this.item.site}).subscribe()
          },
          post_delete: () => {
            this.meta.subItems = [];
            this.loadData({search: this.item.site}).subscribe()
          }
        };
        this.meta.fields.points = {
          type: 'wetlands/points',
          verbose_name_plural: 'Points',
          display_fields: ['parsed_attributes'],
          action_buttons: [
            {
              label: 'Zoom to feature',
              icon: 'fa-magnifying-glass-location',
              func: item => {
                this.zoomToFeature(item, this.layers.points);
              }
            }
          ]
        };
        this.meta.fields.lines = {
          type: 'wetlands/lines',
          verbose_name_plural: 'Lines',
          display_fields: ['parsed_attributes'],
          action_buttons: [
            {
              label: 'Zoom to feature',
              icon: 'fa-magnifying-glass-location',
              func: item => {
                this.zoomToFeature(item, this.layers.lines);
              }
            }
          ]
        };
        this.meta.fields.polygons = {
          type: 'wetlands/polygons',
          verbose_name_plural: 'Polygons',
          display_fields: ['parsed_attributes'],
          action_buttons: [
            {
              label: 'Zoom to feature',
              icon: 'fa-magnifying-glass-location',
              func: item => {
                this.zoomToFeature(item, this.layers.polygons);
              }
            }
          ]
        };
        this.meta.listAttachments = true;
      }),
      filter(() => Object.keys(this.item).length > 0),
      switchMap(() => this.highlightFeatures(this.item))
    );
  }

}
